/**
 * The completeness of one progressively loaded volume, as a plain value.
 *
 * `Image3D` mutates outside React — the stream controller inserts slices into
 * it directly — so no component can subscribe to it. This module is the
 * framework-free half of the bridge: it snapshots the domain object's
 * completeness into an immutable value, compares two snapshots, and rate-limits
 * the stream of snapshots a load produces. The Recoil half lives in
 * `~/state/atoms/imageStreamingAtoms`.
 *
 * Keeping it here, rather than in the atom module, means the controller and its
 * tests never reach for a React tree, and the throttling policy — the part with
 * a safety argument attached — is unit-testable on its own.
 */

import type { Image3D } from '~/domain/patient/image3d';

/**
 * Slice counts, never PHI. Nothing here identifies a patient, a study or an
 * instance; `seriesUid` is carried by the atom key, not by this value.
 */
export type ImageStreamProgress = {
    /** Slices whose pixels have arrived. */
    loadedSliceCount: number;
    /** Slices the manifest says the volume has. */
    expectedSliceCount: number;
    /** Every expected slice has arrived. */
    isComplete: boolean;
    /**
     * Retrieval finished with slices still missing. Terminal: this volume will
     * never become complete, and it is NOT the same state as "still loading".
     */
    isIncompleteFinal: boolean;
    /** Slices whose retrieval failed terminally. Never interpolated. */
    unavailableSliceCount: number;
};

/**
 * What an image that is not progressively loaded reports: a parsed series is
 * complete the moment it exists, so nothing gates on it.
 */
export const COMPLETE_PROGRESS: ImageStreamProgress = {
    loadedSliceCount: 0,
    expectedSliceCount: 0,
    isComplete: true,
    isIncompleteFinal: false,
    unavailableSliceCount: 0,
};

/** Snapshots an image's completeness. Safe for any `Image3D`. */
export function progressOf(image: Image3D): ImageStreamProgress {
    return {
        loadedSliceCount: image.loadedSliceCount,
        expectedSliceCount: image.expectedSliceCount,
        isComplete: image.isComplete,
        isIncompleteFinal: image.isIncompleteFinal,
        unavailableSliceCount: image.unavailableSliceCount,
    };
}

/** Value equality, so an unchanged snapshot never re-renders a subscriber. */
export function progressEquals(
    a: ImageStreamProgress,
    b: ImageStreamProgress
): boolean {
    return (
        a.loadedSliceCount === b.loadedSliceCount &&
        a.expectedSliceCount === b.expectedSliceCount &&
        a.isComplete === b.isComplete &&
        a.isIncompleteFinal === b.isIncompleteFinal &&
        a.unavailableSliceCount === b.unavailableSliceCount
    );
}

/**
 * A state a load does not leave. Both flip a gate or a warning, so neither may
 * ever sit behind a timer.
 */
function isTerminal(progress: ImageStreamProgress): boolean {
    return progress.isComplete || progress.isIncompleteFinal;
}

/** Slice arrivals are coalesced to at most one atom write per this interval. */
export const STREAM_PROGRESS_INTERVAL_MS = 100;

/**
 * Rate-limits progress snapshots on their way to Recoil.
 *
 * A large CT streams hundreds of slices, several per animation frame at the
 * default concurrency of 6, and each atom write re-renders every subscriber —
 * the viewport indication, the sidebar entry and every gated control. A counter
 * that ticks ten times a second is as readable as one that ticks four hundred
 * times, and it leaves the main thread to the scroll the user is doing while
 * the volume fills.
 *
 * The policy has one exception, and it is the whole safety argument:
 * **terminal snapshots are never delayed**. `isComplete` lifts every gate and
 * `isIncompleteFinal` raises a persistent warning; a control that stayed
 * disabled for another 100 ms after the volume completed would be a bug the
 * user cannot explain, and a warning that appeared late would be worse. Only
 * the intermediate "142 of 300" ticks are coalesced.
 *
 * Leading edge plus trailing edge: the first snapshot after a quiet period
 * shows immediately (so the indication appears as soon as streaming starts),
 * and the last snapshot of a burst is never dropped.
 */
export class StreamProgressThrottle {
    private timer: ReturnType<typeof setTimeout> | null = null;
    private pending: ImageStreamProgress | null = null;
    private lastEmitted: ImageStreamProgress | null = null;
    private disposed = false;

    /**
     * @param emit Receives the snapshots that survive throttling.
     * @param intervalMs Minimum spacing between non-terminal emissions.
     */
    public constructor(
        private readonly emit: (progress: ImageStreamProgress) => void,
        private readonly intervalMs: number = STREAM_PROGRESS_INTERVAL_MS
    ) {}

    /** Offers a snapshot. May emit now, later, or not at all (unchanged). */
    public report(progress: ImageStreamProgress): void {
        if (this.disposed) {
            return;
        }

        if (isTerminal(progress)) {
            this.pending = null;
            this.clearTimer();
            this.emitIfChanged(progress);
            return;
        }

        this.pending = progress;
        if (this.timer === null) {
            this.flushPending();
            this.timer = setTimeout(() => {
                this.timer = null;
                this.flushPending();
            }, this.intervalMs);
        }
    }

    /**
     * Stops the timer and drops anything still pending WITHOUT emitting it.
     *
     * The callers are abort paths — patient close, viewer teardown — where the
     * subscriber is going away too. Emitting into a torn-down tree is the one
     * thing this must not do.
     */
    public dispose(): void {
        this.disposed = true;
        this.pending = null;
        this.clearTimer();
    }

    private flushPending(): void {
        const progress = this.pending;
        this.pending = null;
        if (progress !== null) {
            this.emitIfChanged(progress);
        }
    }

    private emitIfChanged(progress: ImageStreamProgress): void {
        if (
            this.lastEmitted !== null &&
            progressEquals(this.lastEmitted, progress)
        ) {
            return;
        }
        this.lastEmitted = progress;
        this.emit(progress);
    }

    private clearTimer(): void {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
